import { isString } from "generic-type-guard";
import { call, put, SagaGenerator, takeEvery } from "typed-redux-saga";

import { LocalizableArgs } from "@com.mgmtp.a12.utils/utils-localization/lib/main";

import { createCustomNotificationAction } from "../../app/actions";
import { fetchSagaFactory } from "../../app/sagas/fetchSagaFactory";
import { Manifest } from "../../app/slices/manifest/types";
import { pollExportCsvResultTimeoutMS } from "../../config";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../localization";
import { assertTrue } from "../../utilities/assert";
import { toDateToPrettyString } from "../../utilities/dateTimeUtils";
import { isFachverfahrenManifest } from "../../utilities/loadAppmodelTypeguards";
import { putErrorNotification } from "../../utilities/notificationUtils";

import { exportApplicationData } from "../processOverview/store/actions";

export function* exportApplicationDataSaga(): SagaGenerator<void> {
	yield* takeEvery(exportApplicationData, function* handle(action) {
		const localizableArgs: LocalizableArgs = {
			appKey: { type: "plain", value: action.payload.manifest.identification.key },
			timestamp: { type: "plain", value: toDateToPrettyString(new Date()) },
			appType: {
				type: "plain",
				value: isFachverfahrenManifest(action.payload.manifest) ? "Fachverfahrens-Vorgänge" : "Stammdaten"
			}
		};
		yield* call(startExport, action.payload.manifest, action.payload.anonym, localizableArgs);
	});
}

function* startExport(manifest: Manifest, anonym: boolean, localizableArgs: LocalizableArgs): SagaGenerator<void> {
	yield* call(
		fetchSagaFactory(
			{
				relativeUrl: `/application/export/xlsx?mainDocumentModelId=${manifest.models.mainDocumentModelId}&anonym=${anonym}`,
				method: "POST"
			},
			function* handleResponse(response) {
				const jobId: unknown = yield* call(response.json.bind(response));
				assertTrue(isString(jobId));

				yield* put(
					createCustomNotificationAction({
						title: { key: RESOURCE_KEYS.features.notification.title.info },
						message: localizableFromLocaleResources(
							RESOURCE_KEYS.features.notification.info.exportCsv,
							localizableArgs
						),
						icon: "info",
						severity: "info",
						group: `exportData:${jobId}`
					})
				);

				yield* call(getExportResult, manifest.identification.key, jobId, localizableArgs);
			},
			function* handleError(error) {
				yield* call(putArchiveError, localizableArgs, error);
			}
		)
	);
}

function* getExportResult(fvKey: string, jobId: string, localizableArgs: LocalizableArgs): SagaGenerator<void> {
	yield* call(
		fetchSagaFactory(
			{
				relativeUrl: `/application/export/xlsx?jobId=${jobId}`,
				method: "POST",
				customHeaders: [["Accept", "application/x-zip-compressed"]]
			},
			function* handleResponse(response) {
				if (response.status === 202) {
					yield* call(() => new Promise(x => setTimeout(x, pollExportCsvResultTimeoutMS)));
					yield* call(getExportResult, fvKey, jobId, localizableArgs);
					return;
				}

				const blob = yield* call(response.blob.bind(response));
				const downloadUrl = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = downloadUrl;
				link.download = fvKey + "_excel_export.zip";
				link.click();
				URL.revokeObjectURL(downloadUrl);

				yield* put(
					createCustomNotificationAction({
						title: { key: RESOURCE_KEYS.features.notification.title.success },
						message: localizableFromLocaleResources(
							RESOURCE_KEYS.features.notification.success.exportCsv,
							localizableArgs
						),
						icon: "done",
						severity: "success",
						group: `exportData:${jobId}`
					})
				);
			},
			function* handleError(error) {
				yield* call(putArchiveError, localizableArgs, error, jobId);
			}
		)
	);
}

function* putArchiveError(localizableArgs: LocalizableArgs, error: any, jobId?: string) {
	yield* call(
		putErrorNotification,
		localizableFromLocaleResources(
			error.status === 503
				? RESOURCE_KEYS.features.notification.error.exportCsv.queueFull
				: RESOURCE_KEYS.features.notification.error.exportCsv.serverError,
			localizableArgs
		),
		error,
		undefined,
		`exportData:${jobId}`
	);
}
